import { useState } from "react";
import { Button, Group, NumberInput, Select, Stack } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { changeRuleStatus } from "../../../../../services/rules";
import { periodItems } from "../../../../../utils/vars";
import { getAxiosResponseError } from "../../../../../utils/functions";
import { ruleQueryKey } from "../../../../../components/QueryBuilder/hooks/queryKeys";

const actions = [
  { value: "REJECT", label: "REJECT" },
  { value: "REVIEW", label: "REVIEW" },
];

function PTMForm({ isActive, minDate, isPending, onSubmit }) {
  const { t } = useTranslation();
  const [periodCount, setPeriodCount] = useState(null);
  const [period, setPeriod] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const canConfirm = isActive
    ? Boolean(selectedDate)
    : Boolean(periodCount && period && selectedDate);

  const handleConfirm = () =>
    onSubmit({ date: selectedDate, period, periodCount, action: null });

  return (
    <Group align="flex-end" gap="md" wrap="wrap">
      {!isActive && (
        <>
          <NumberInput
            label="Period Count"
            min={1}
            value={periodCount ?? ""}
            onChange={(v) => setPeriodCount(v === "" ? null : Number(v))}
            w={160}
          />
          <Select
            label={t("multipleSearch.detailsPopup.period")}
            data={periodItems.map((p) => ({ value: p.value, label: p.label }))}
            value={period}
            onChange={setPeriod}
            w={140}
          />
        </>
      )}
      <DateInput
        label={t("multipleSearch.detailsPopup.fromDate")}
        value={selectedDate ? new Date(selectedDate) : null}
        onChange={(v) =>
          setSelectedDate(v ? dayjs(v).format("YYYY-MM-DD") : null)
        }
        minDate={minDate}
        w={180}
      />
      <Button
        disabled={!canConfirm}
        loading={isPending}
        onClick={handleConfirm}
      >
        Confirm
      </Button>
    </Group>
  );
}

/** RTM form: Action, From date. */
function RTMForm({ isActive, minDate, isPending, onSubmit }) {
  const { t } = useTranslation();
  const [action, setAction] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const canConfirm = isActive
    ? Boolean(selectedDate)
    : Boolean(action && selectedDate);

  const handleConfirm = () =>
    onSubmit({ date: selectedDate, period: null, periodCount: null, action });

  return (
    <Group align="flex-end" gap="md" wrap="wrap">
      {!isActive && (
        <Select
          label="Action"
          data={actions}
          value={action}
          onChange={setAction}
          w={140}
        />
      )}
      <DateInput
        label={t("multipleSearch.detailsPopup.fromDate")}
        value={selectedDate ? new Date(selectedDate) : null}
        onChange={(v) =>
          setSelectedDate(v ? dayjs(v).format("YYYY-MM-DD") : null)
        }
        minDate={minDate}
        w={180}
      />
      <Button
        disabled={!canConfirm}
        loading={isPending}
        onClick={handleConfirm}
      >
        Confirm
      </Button>
    </Group>
  );
}

export default function ActivateDeactivateSection({
  ruleId,
  isActive,
  expanded,
  onToggle,
  previousVersionDate,
  onSuccess,
  typeOfRule,
}) {
  const queryClient = useQueryClient();
  const isRTM = typeOfRule === "RTM";

  const mutation = useMutation({
    mutationFn: changeRuleStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ruleQueryKey(ruleId) });
      queryClient.invalidateQueries({ queryKey: ["rule-history", ruleId] });
      toast.success(isActive ? "Rule deactivated" : "Rule activated");
      onSuccess?.();
    },
    onError: (err) => toast.error(getAxiosResponseError(err)),
  });

  const isPreviousDateValid =
    previousVersionDate && !Number.isNaN(previousVersionDate.getTime?.());
  const minDate = isPreviousDateValid ? previousVersionDate : new Date();

  const handleSubmit = (values) =>
    mutation.mutate({
      id: ruleId,
      status: isActive ? "DEACTIVATED" : "ACTIVATED",
      ...values,
    });

  const formProps = {
    isActive,
    minDate,
    isPending: mutation.isPending,
    onSubmit: handleSubmit,
  };

  return (
    <Stack gap="xs">
      <Group justify="flex-start">
        <Button variant="outline" onClick={onToggle}>
          {isActive ? "Deactivate" : "Activate"}
        </Button>
      </Group>
      {expanded &&
        (isRTM ? <RTMForm {...formProps} /> : <PTMForm {...formProps} />)}
    </Stack>
  );
}
